// src/components/sections/ProjectDetail.js
import React, { useState, useEffect } from 'react';
import { X, ArrowLeft, ArrowRight, Github, ExternalLink } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';

const ProjectDetail = ({ project, onClose, projectsData, currentIndex }) => {
  const { darkMode } = useTheme();
  const { language } = useLanguage();
  const [activeImage, setActiveImage] = useState(0);
  const [nextProject, setNextProject] = useState(null);
  const [prevProject, setPrevProject] = useState(null);

  useEffect(() => {
    // Calculer le projet précédent et suivant
    if (projectsData && projectsData.length > 0) {
      const nextIdx = (currentIndex + 1) % projectsData.length;
      const prevIdx = (currentIndex - 1 + projectsData.length) % projectsData.length;
      setNextProject(projectsData[nextIdx]);
      setPrevProject(projectsData[prevIdx]);
    }

    // Désactiver le défilement de la page quand le modal est ouvert
    document.body.style.overflow = 'hidden';
    
    return () => {
      // Réactiver le défilement quand le modal est fermé
      document.body.style.overflow = 'auto';
    };
  }, [currentIndex, projectsData]);

  // Gérer la navigation entre les images
  const handleImageNav = (direction) => {
    if (direction === 'next') {
      setActiveImage((prev) => (prev + 1) % project.gallery.length);
    } else {
      setActiveImage((prev) => (prev - 1 + project.gallery.length) % project.gallery.length);
    }
  };

  // Gérer la navigation entre les projets
  const handleProjectNav = (direction) => {
    if (direction === 'next' && nextProject) {
      onClose(nextProject.id);
    } else if (direction === 'prev' && prevProject) {
      onClose(prevProject.id);
    }
  };

  // Gérer les touches du clavier
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight') {
        if (e.shiftKey) {
          handleProjectNav('next');
        } else {
          handleImageNav('next');
        }
      } else if (e.key === 'ArrowLeft') {
        if (e.shiftKey) {
          handleProjectNav('prev');
        } else {
          handleImageNav('prev');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [project, activeImage, nextProject, prevProject]);

  if (!project) return null;

  // Traductions locales
  const translations = {
    fr: {
      description: "Description",
      features: "Fonctionnalités",
      technologies: "Technologies",
      sourceCode: "Code source",
      liveDemo: "Démo en ligne",
      prevProject: "Projet précédent",
      nextProject: "Projet suivant"
    },
    en: {
      description: "Description",
      features: "Features",
      technologies: "Technologies",
      sourceCode: "Source code",
      liveDemo: "Live demo",
      prevProject: "Previous project",
      nextProject: "Next project"
    }
  };

  const t = translations[language];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
          darkMode ? 'bg-black bg-opacity-80' : 'bg-gray-800 bg-opacity-75'
        }`}
        onClick={() => onClose()}
      >
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 20, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className={`relative max-w-6xl w-full max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${
            darkMode ? 'bg-gray-900' : 'bg-white'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Bouton de fermeture */}
          <button
            onClick={() => onClose()}
            className={`absolute right-4 top-4 z-10 p-2 rounded-full ${
              darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-200 hover:bg-gray-300'
            } transition-colors duration-200`}
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Galerie d'images */}
            <div className="p-6">
              <div className="relative h-64 md:h-96 mb-4 rounded-lg overflow-hidden">
                {/* Image principale */}
                <img 
                  src={project.gallery ? project.gallery[activeImage] : project.image} 
                  alt={`${project.title[language]} - vue ${activeImage + 1}`}
                  className="w-full h-full object-cover"
                />
                
                {/* Navigation d'images */}
                {project.gallery && project.gallery.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleImageNav('prev');
                      }}
                      className={`absolute left-2 top-1/2 transform -translate-y-1/2 p-2 rounded-full ${
                        darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-200'
                      } shadow-md transition-colors duration-200`}
                      aria-label="Image précédente"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleImageNav('next');
                      }}
                      className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-2 rounded-full ${
                        darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-200'
                      } shadow-md transition-colors duration-200`}
                      aria-label="Image suivante"
                    >
                      <ArrowRight className="w-5 h-5" />
                    </button>
                    
                    {/* Indicateurs d'images */}
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2">
                      {project.gallery.map((_, index) => (
                        <button
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveImage(index);
                          }}
                          className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                            activeImage === index
                              ? (darkMode ? 'bg-blue-500' : 'bg-blue-600')
                              : (darkMode ? 'bg-gray-600' : 'bg-gray-300')
                          }`}
                          aria-label={`Voir image ${index + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Miniatures */}
              {project.gallery && project.gallery.length > 1 && (
                <div className="flex overflow-x-auto space-x-2 pb-2">
                  {project.gallery.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveImage(index)}
                      className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-colors duration-200 ${
                        activeImage === index
                          ? (darkMode ? 'border-blue-500' : 'border-blue-600')
                          : (darkMode ? 'border-gray-700' : 'border-gray-200')
                      }`}
                    >
                      <img 
                        src={img} 
                        alt={`Miniature ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Informations du projet */}
            <div className="p-6">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">{project.title[language]}</h2>
              
              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                {project.tags.map((tag, idx) => {
                  // Définir une couleur en fonction du tag
                  let colorClass;
                  if (tag.includes('Spring')) {
                    colorClass = darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800';
                  } else if (tag.includes('React')) {
                    colorClass = darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800';
                  } else if (tag.includes('Docker') || tag.includes('Kubernetes') || tag.includes('CI/CD')) {
                    colorClass = darkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-800';
                  } else {
                    colorClass = darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800';
                  }
                  
                  return (
                    <span 
                      key={idx} 
                      className={`px-3 py-1 rounded-full text-xs ${colorClass}`}
                    >
                      {tag}
                    </span>
                  );
                })}
              </div>
              
              {/* Description détaillée */}
              <div className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <h3 className="text-xl font-semibold mb-2">{t.description}</h3>
                <p className="mb-4">{project.longDescription[language]}</p>
              </div>
              
              {/* Fonctionnalités */}
              <div className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <h3 className="text-xl font-semibold mb-2">{t.features}</h3>
                <ul className="list-disc pl-5 space-y-1">
                  {project.features[language].map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </div>
              
              {/* Technologies */}
              <div className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <h3 className="text-xl font-semibold mb-2">{t.technologies}</h3>
                <ul className="list-disc pl-5 space-y-1">
                  {project.technologies.map((tech, idx) => (
                    <li key={idx}>{tech}</li>
                  ))}
                </ul>
              </div>
              
              {/* Liens */}
              <div className="flex flex-wrap gap-4 mt-8">
                {project.githubLink && (
                  <a
                    href={project.githubLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center px-4 py-2 rounded-md transition-colors duration-200 ${
                      darkMode 
                        ? 'bg-gray-800 hover:bg-gray-700 text-white' 
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                    }`}
                  >
                    <Github className="w-5 h-5 mr-2" />
                    {t.sourceCode}
                  </a>
                )}
                
                {project.demoLink && project.demoLink !== '#' && (
                  <a
                    href={project.demoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center px-4 py-2 rounded-md transition-colors duration-200 ${
                      darkMode 
                        ? 'bg-blue-700 hover:bg-blue-600 text-white' 
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    <ExternalLink className="w-5 h-5 mr-2" />
                    {t.liveDemo}
                  </a>
                )}
              </div>
            </div>
          </div>
          
          {/* Navigation entre projets */}
          <div className={`p-4 border-t ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
            <div className="flex justify-between">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleProjectNav('prev');
                }}
                className={`flex items-center px-3 py-1 rounded transition-colors duration-200 ${
                  darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
                }`}
                disabled={!prevProject}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {t.prevProject}: {prevProject && prevProject.title[language]}
              </button>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleProjectNav('next');
                }}
                className={`flex items-center px-3 py-1 rounded transition-colors duration-200 ${
                  darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
                }`}
                disabled={!nextProject}
              >
                {t.nextProject}: {nextProject && nextProject.title[language]}
                <ArrowRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ProjectDetail;